import { useState } from 'react'
import cn from 'classnames'
import { Checkbox } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import {
  useSelectedCategories,
  useCategoriesStatic,
  useMotivationToCategoriesMap,
  useCategoryNameToIdMap,
  useMotivations,
  type Motivation as MotivationType,
  useMotivationsCopyMap,
  AI_MOTIVATION_KEY,
  AI_MOTIVATION_QUERY_PARAM,
} from '../../../../hooks/useCategorySelections'
import { ButtonsSection, InteractiveStep } from '../../components'
import { QuizMachineContext } from '../../context'
import { useQuizEvents } from '../../events'

export const Motivation = () => {
  const { t } = useTranslation('@mc/next')

  const { trackOptionSelected } = useQuizEvents()
  const { trackPrimerAnswerSubmitted } = useQuizEvents()
  const categories = useCategoriesStatic()
  const catNameToId = useCategoryNameToIdMap()
  const motivations = useMotivations()
  const motivationToCatName = useMotivationToCategoriesMap()
  const motivationCopyMap = useMotivationsCopyMap(motivations, t)

  // Read AI motivation flag from XState context (SSR-consistent;
  // refreshed from URL by the machine on state transitions). Avoids hydration
  // mismatches caused by reading window.location.search during useState init.
  const hasAiMotivationParam = QuizMachineContext.useSelector(
    (s) => s.context.hasAiMotivationParam,
  )

  const { selectedCategories, setSelectedCategories, syncToUrl } =
    useSelectedCategories()

  // initialize selected motivations for this quiz step using selected categories (from URL)
  const inferMotivationsFromURLCategories = () => {
    const initMotivations = new Set<MotivationType>()
    Object.entries(motivationToCatName).forEach(([m, catNames]) => {
      if (catNames?.some((cat) => selectedCategories.has(cat))) {
        initMotivations.add(m)
      }
    })
    // Restore AI motivation from context on back navigation
    if (hasAiMotivationParam) {
      initMotivations.add(AI_MOTIVATION_KEY)
    }
    return initMotivations
  }

  const [selectedMotivations, setSelectedMotivations] = useState(
    inferMotivationsFromURLCategories(),
  )

  const isOptionSelected = (motivationName: MotivationType) =>
    selectedMotivations.has(motivationName)

  const selectedOptions = motivations
    .filter(isOptionSelected)
    .map((m) => motivationCopyMap[m])

  const canSubmitAnswer = selectedOptions.length > 0

  const selectOption = (motivation: MotivationType) => {
    const checked = isOptionSelected(motivation)
    setSelectedMotivations((oldSet) => {
      const currentMotivations = new Set(oldSet)
      if (currentMotivations.has(motivation)) {
        currentMotivations.delete(motivation)
      } else {
        currentMotivations.add(motivation)
      }

      // newSet is the current list of selected motivations
      // so we map them to the categories they represent
      setSelectedCategories(() => {
        const newMap = new Map<string, number>()
        currentMotivations.forEach((m) => {
          const mappedCategories = motivationToCatName[m] || []
          mappedCategories.forEach((catName) => {
            const catId = Number(catNameToId.get(catName))
            newMap.set(catName, catId)
          })
        })
        return newMap
      })

      return currentMotivations
    })
    trackOptionSelected({
      value: motivationCopyMap[motivation],
      // (inverted) At this point option is going to be checked but is not currently checked
      checked: !checked,
    })
  }

  const onClickNext = () => {
    const otherCategoryIds =
      motivationToCatName.other?.map((otherCatName) =>
        catNameToId.get(otherCatName),
      ) || []
    const selectedCategoryIds = [...selectedCategories.values()]
    const trackedCategoryIds = selectedCategoryIds.filter(
      (categoryId) => !otherCategoryIds.includes(categoryId),
    )
    const trackedCategories = trackedCategoryIds.map(
      (categoryId) =>
        categories?.find((c) => Number(c.id) === categoryId)?.name ??
        '<null category>', // null should never happen
    )
    trackPrimerAnswerSubmitted({
      selectedOptions,
      selectedCategoryIds: trackedCategoryIds,
      selectedCategories: trackedCategories,
    })

    // Atomically set/remove AI motivation query param alongside category params
    // so the results page can distinguish AI from regular Science & Technology
    // selection (single replaceState call)
    syncToUrl({
      [AI_MOTIVATION_QUERY_PARAM]: selectedMotivations.has(AI_MOTIVATION_KEY)
        ? 'true'
        : null,
    })
  }

  return (
    <InteractiveStep title={t('findMyClasses.steps.motivation.title')}>
      <div className='mc-my-7'>
        <div className='row no-gutters-vertical justify-content-center'>
          <div className='col-12 col-md-10 col-lg-7'>
            <div className='mc-my-2'>
              {motivations.map((value, id) => (
                <Checkbox
                  key={id}
                  className={cn(
                    'mc-bg-gray-100',
                    'mc-p-6 mc-my-1',
                    'mc-text--left',
                    {
                      'mc-bg-gray-300': isOptionSelected(value),
                      'mc-corners-top-left--sm mc-corners-top-right--sm':
                        id === 0,
                      'mc-corners-bottom-left--sm mc-corners-bottom-right--sm':
                        id === motivations.length - 1,
                    },
                  )}
                  checked={isOptionSelected(value)}
                  label={motivationCopyMap[value]}
                  option={id}
                  onChange={() => selectOption(value)}
                  name={`option-${id}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <ButtonsSection
        disabled={!canSubmitAnswer}
        onNext={onClickNext}
        disabledText={t(
          'findMyClasses.steps.motivation.a11y.disabledDisclaimer',
        )}
        showSkip
      />
    </InteractiveStep>
  )
}
